import * as React from "react"
import useKeyboardLift from "@/hooks/useKeyboardLift";
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[1200] bg-black/40 data-[state=open]:duration-[420ms] data-[state=closed]:duration-[320ms] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// Apple HIG: on phones (<640px) every dialog is a bottom sheet with a grabber; tablets/desktop keep a centred card.
const SHEET = "inset-x-0 bottom-0 top-auto max-sm:left-0 max-sm:right-0 max-sm:w-full max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-b-none max-h-[92dvh] overflow-y-auto rounded-t-[20px] rounded-b-none border-x-0 border-b-0 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-7 data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom data-[state=open]:fade-in-100 data-[state=closed]:fade-out-100 data-[state=open]:duration-[420ms] data-[state=closed]:duration-[320ms] yorm-sheet";
const CENTERED = "sm:inset-auto sm:left-[50%] sm:top-[50%] sm:bottom-auto sm:max-h-[90vh] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-2xl sm:border sm:pt-6 sm:pb-6 sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=open]:fade-in-0 sm:data-[state=closed]:fade-out-0 sm:data-[state=open]:duration-200 sm:data-[state=closed]:duration-150";

// Phone grab bar: drag the sheet down to dismiss (iOS style). Slides out itself, then closes instantly.
const SheetGrabber = ({ contentRef, closeRef }) => {
  const drag = React.useRef(null);
  const el = () => contentRef.current;
  const onDown = (e) => {
    if (!el()) return;
    drag.current = { y: e.clientY, t: performance.now(), dy: 0 };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    el().style.transition = "none";
  };
  const onMove = (e) => {
    if (!drag.current || !el()) return;
    const dy = Math.max(0, e.clientY - drag.current.y);
    drag.current.dy = dy;
    el().style.transform = `translateY(${dy}px)`;
  };
  const onUp = () => {
    const d = drag.current;
    drag.current = null;
    const node = el();
    if (!d || !node) return;
    const speed = d.dy / Math.max(1, performance.now() - d.t);
    const dismiss = d.dy > Math.min(140, node.offsetHeight * 0.25) || (d.dy > 30 && speed > 0.6);
    node.style.transition = "transform 260ms cubic-bezier(0.32, 0.72, 0, 1)";
    if (!dismiss) { node.style.transform = "translateY(0)"; return; }
    node.style.transform = "translateY(105%)";
    setTimeout(() => { node.style.animation = "none"; closeRef.current?.click(); }, 240);
  };
  return (
    <div
      className="absolute left-1/2 top-0 z-[95] h-7 w-32 -translate-x-1/2 pt-2 cursor-grab touch-none sm:hidden"
      onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
      data-testid="sheet-grabber">
      <div className="ios-grabber" aria-hidden="true" />
    </div>
  );
};

// Phones: drag down from anywhere on a slide-up to dismiss it, once its scroll area is at the top.
const NO_DRAG = "input, textarea, select, [contenteditable='true'], .leaflet-container, [data-no-sheet-drag], [role='slider']";
const scrolledDown = (target, root) => {
  for (let n = target; n && n !== root.parentElement; n = n.parentElement) {
    if (n.scrollTop > 0 && n.scrollHeight > n.clientHeight) return true;
    if (n === root) break;
  }
  return false;
};
const useSheetDrag = (contentRef, closeRef, enabled) => {
  React.useEffect(() => {
    const node = contentRef.current;
    if (!enabled || !node || !window.matchMedia?.("(max-width: 639px)").matches) return undefined;
    let d = null;
    const start = (e) => {
      const t = e.touches[0];
      if (e.touches.length > 1 || e.target.closest?.(NO_DRAG) || e.target.closest?.("[data-testid='sheet-grabber']")) { d = null; return; }
      d = { x: t.clientX, y: t.clientY, t: performance.now(), dy: 0, on: false, blocked: scrolledDown(e.target, node) };
    };
    const move = (e) => {
      if (!d || d.blocked) return;
      const t = e.touches[0];
      const dy = t.clientY - d.y;
      const dx = Math.abs(t.clientX - d.x);
      if (!d.on) {
        if (dy < 8 || dx > dy) { if (dy < -4 || dx > 8) d = null; return; }
        d.on = true; d.y = t.clientY; d.t = performance.now();
        node.style.transition = "none";
      }
      e.preventDefault();
      d.dy = Math.max(0, t.clientY - d.y);
      node.style.transform = `translateY(${d.dy}px)`;
    };
    const end = () => {
      const g = d; d = null;
      if (!g?.on) return;
      const speed = g.dy / Math.max(1, performance.now() - g.t);
      const dismiss = g.dy > Math.min(140, node.offsetHeight * 0.25) || (g.dy > 30 && speed > 0.6);
      node.style.transition = "transform 260ms cubic-bezier(0.32, 0.72, 0, 1)";
      if (!dismiss) { node.style.transform = "translateY(0)"; return; }
      node.style.transform = "translateY(105%)";
      setTimeout(() => {
        node.style.animation = "none";
        closeRef.current?.click();
        // Sheet refused to close (must be completed first): spring back.
        setTimeout(() => {
          if (node.isConnected && node.getAttribute("data-state") === "open") {
            node.style.animation = ""; node.style.transform = "translateY(0)";
          }
        }, 120);
      }, 240);
    };
    node.addEventListener("touchstart", start, { passive: true });
    node.addEventListener("touchmove", move, { passive: false });
    node.addEventListener("touchend", end);
    node.addEventListener("touchcancel", end);
    return () => {
      node.removeEventListener("touchstart", start);
      node.removeEventListener("touchmove", move);
      node.removeEventListener("touchend", end);
      node.removeEventListener("touchcancel", end);
    };
  }, [contentRef, closeRef, enabled]);
};

// Opening a slide-up must not pop the keyboard: keep focus off text boxes until the user taps one.
export const keepKeyboardDown = (node) => (e) => {
  e.preventDefault();
  requestAnimationFrame(() => {
    const a = document.activeElement;
    if (node?.current?.contains(a) && a.matches("input, textarea, [contenteditable='true']")) a.blur();
    node?.current?.focus({ preventScroll: true });
  });
};

const DialogContent = React.forwardRef(({ className, children, hideCloseButton, draggable, sheet = true, solid = false, onOpenAutoFocus, style, keyboardManaged = false, ...props }, ref) => {
  const contentRef = React.useRef(null);
  // Sheets with their own keyboard handling (Yorm Ai) pass a style; others ride on top of the keys.
  const closeRef = React.useRef(null);
  const [mounted, setMounted] = React.useState(null);
  const lift = useKeyboardLift(sheet && !keyboardManaged && !!mounted, contentRef);
  const setRefs = React.useCallback((node) => {
    contentRef.current = node;
    setMounted(node);
    if (typeof ref === "function") ref(node); else if (ref) ref.current = node;
  }, [ref]);
  // Drag-to-close on every dismissible slide-up; sheets without the X opt in with `draggable`.
  useSheetDrag(contentRef, closeRef, sheet && (draggable ?? !hideCloseButton) && !!mounted);
  return (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={setRefs}
      className={cn(
        "fixed z-[1201] grid grid-cols-[minmax(0,1fr)] w-full max-w-lg gap-4 border border-black/5 bg-background p-6 shadow-[0_-8px_30px_rgba(0,0,0,0.12)] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        sheet ? cn(SHEET, CENTERED) : "left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] rounded-2xl data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        !solid && "pay-glass",
        className
      )}
      onOpenAutoFocus={(e) => { onOpenAutoFocus?.(e); if (!e.defaultPrevented) keepKeyboardDown(contentRef)(e); }}
      style={lift || style ? { ...style, ...lift } : undefined}
      {...props}>
      {sheet && (draggable ?? !hideCloseButton) && <SheetGrabber contentRef={contentRef} closeRef={closeRef} />}
      {sheet && <DialogPrimitive.Close ref={closeRef} tabIndex={-1} aria-hidden="true" className="hidden" />}
      {children}
      {!hideCloseButton && (
        <DialogPrimitive.Close
          className="absolute right-3.5 top-3.5 flex h-7 w-7 items-center justify-center rounded-full bg-[rgba(118,118,128,0.12)] text-[rgba(60,60,67,0.6)] transition-colors hover:bg-[rgba(118,118,128,0.2)] focus:outline-none disabled:pointer-events-none"
          data-testid="dialog-close-btn">
          <X className="h-3.5 w-3.5" strokeWidth={2.5} />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-[17px] font-semibold leading-snug tracking-[-0.01em]", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-[15px] leading-5 text-[rgba(60,60,67,0.6)]", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
